import {
  apiRequest,
  userAppApiRequest,
  socialApiRequest,
  socialDashboardApiRequest,
  assetApiRequest
} from './apiWrapper'
import { encryptPayload } from '../../../../apps/super-app/src/crypto/encryptPayload.js'
import { getApiHeaders } from './deviceRegistrationApi'
import { uploadProfileImage } from './loginApi'

import { getPublicIP } from '../utils/sonyAudioUtils'
import { identifyClarityUser } from '../utils/clarity'

export async function fetchUserProfileDetails(user_id) {
  const accessToken = localStorage.getItem('access')
  const headers = getApiHeaders()
  let userIp = ''
  try {
    userIp = await getPublicIP()
  } catch (err) {
    console.warn('Failed to get public IP for profile API:', err)
  }

  const response = await userAppApiRequest({
    method: 'get',
    // url: `profile/${user_id}`
    url: 'user/v1/auth/user_profile/profile',
    headers: {
      ...headers,
      'x-api-token': accessToken,
      ...(userIp ? { 'x-forwarded-for': userIp } : {})
    }
  })
   const identityProviders = response?.data?.identity_providers || {}
   const isSocialUser = Object.values(identityProviders).some(Boolean)
   localStorage.setItem('isSocialUser', JSON.stringify(isSocialUser))

   const currentCountry = response?.data?.current_country || response?.current_country
   if (currentCountry) {
     localStorage.setItem('user_current_country', currentCountry)
     localStorage.setItem('currentCountry', currentCountry)
   }

   identifyClarityUser(user_id, response?.data?.name)
   return response
}

export async function fetchFilterOptions() {
  const accessToken = localStorage.getItem('access')
  const response = await socialApiRequest({
    method: 'get',
    url: 'filter-options',
    headers: {
      'x-api-token': accessToken,
    }
  })
  return response
}

export async function fetchUserDashboardDetails(
  user_id,
  content_type = ['image', 'audio', 'video'],
  limit = 24,
  offset = 0,
  sort_by = 'newest_first',
  created_with = [],
  verification_status = 'approved'
) {
  const normalizedContentType = Array.isArray(content_type)
    ? content_type
    : [content_type]
  const normalizedStatus = verification_status === 'approved' ? 'completed' : verification_status

  const response = await socialDashboardApiRequest({
    method: 'post',
    url: `content/user?limit=${limit}&offset=${offset}`,
    data: {
      user_id: user_id,
      content_type: normalizedContentType,
      sort_by,
      created_with,
      verification_status: normalizedStatus
    }
  })

  if (response?.success && response?.data) {
    const user_details = response?.user_details || response?.data?.user_details
    const contents = Array.isArray(response?.data)
      ? response.data
      : (response?.data?.results || response?.data?.data || [])

    const latest_video = []
    const latest_audio = []
    const latest_images = []

    contents.forEach((item) => {
      if (item.content_type === 'video') {
        latest_video.push(item)
      } else if (item.content_type === 'audio') {
        latest_audio.push(item)
      } else if (item.content_type === 'photo' || item.content_type === 'image') {
        latest_images.push(item)
      }
    })
    return {
      ...response,
      user_info: {
        id: user_details?.id,
        name: user_details?.name,
        username: user_details?.username,
        profile_picture:
          user_details?.profile_picture || user_details?.profile_url,
        about_me: user_details?.about,
        created_at: user_details?.created_at
      },
      followers: user_details?.followers_count || 0,
      following: user_details?.following_count || 0,
      creations: user_details?.creation_count || 0,
      latest_creations: contents,
      latest_video,
      latest_audio,
      latest_images
    }
  }

  return response
}

export async function fetchUserDashboard(
  user_id,
  verification_status,
  content_type,
  limit = 24,
  offset = 0,
  sort_by,
  created_with
) {
  const params = { user_id, verification_status, limit, offset }

  if (content_type) {
    params.content_type = Array.isArray(content_type)
      ? content_type.join(',')
      : content_type
  }

  if (sort_by) {
    params.sort_by = sort_by
  }

  if (created_with) {
    params.created_with = Array.isArray(created_with)
      ? created_with.join(',')
      : created_with
  }

  const response = await apiRequest({
    method: 'get',
    url: `content_dashboard/`,
    params,
    skipCache: true
  })
  return response
}

export async function fetchUserUpdateDetails(formData) {
  const headers = getApiHeaders()
  const plainData = {}

  const isFormData =
    formData &&
    (formData instanceof FormData ||
      typeof formData.append === 'function' ||
      Object.prototype.toString.call(formData) === '[object FormData]')

  if (isFormData) {
    if (typeof formData.forEach === 'function') {
      formData.forEach((value, key) => {
        plainData[key] = value
      })
    } else if (typeof formData.entries === 'function') {
      for (const [key, value] of formData.entries()) {
        plainData[key] = value
      }
    }
  } else {
    Object.keys(formData).forEach((key) => {
      plainData[key] = formData[key]
    })
  }

  // Handle profile image upload if it's a file
  if (plainData.profile_image instanceof File) {
    try {
      const uploadResp = await uploadProfileImage(plainData.profile_image)
      if (uploadResp?.success && uploadResp?.data?.temp_key) {
        plainData.profile_image = uploadResp.data.temp_key
      } else {
        delete plainData.profile_image
      }
    } catch (error) {
      console.error('Profile image upload failed:', error)
      delete plainData.profile_image
    }
  } else if (typeof plainData.profile_image !== 'string') {
    delete plainData.profile_image
  }

  const accessToken = localStorage.getItem('access')
  const response = await userAppApiRequest({
    method: 'put',
    data: plainData,
    url: 'user/v1/auth/user_profile/profile',
    headers: {
      ...headers,
      'x-api-token': accessToken
    }
  })
  return response
}

export async function fetchLikeDetails() {
  const response = await apiRequest({
    method: 'get',
    url: `/likes/`
  })
  return response
}

export async function deleteContentItems(content_ids) {
  const ids = Array.isArray(content_ids) ? content_ids : [content_ids]
  const response = await apiRequest({
    method: 'delete',
    url: `/content/`,
    data: {
      content_ids: ids.filter(Boolean)
    }
  })
  return response
}
export async function bulkDeleteContent(content_ids) {
  const response = await socialApiRequest({
    method: 'delete',
    url: `/content`,
    data: {
      content_ids: Array.isArray(content_ids) ? content_ids : [content_ids]
    }
  })
  return response
}

export async function fetchUserDrafts(
  content_type = ["image", "audio", 'video'],
  sort_by = "newest_first",
  created_with = [],
  limit = 20,
  offset = 0) {
  const normalizedContentType = Array.isArray(content_type)
    ? content_type
    : [content_type]
  return socialApiRequest({
    method: 'post',
    url: `save-draft?limit=${limit}&offset=${offset}`,
    data: {
      content_type: normalizedContentType,
      sort_by,
      created_with
    },
  })
}



export async function fetchBannerData(limit = 20, offset = 0,type="all") {
  const headers = getApiHeaders()
  const response = await userAppApiRequest({
    method: 'get',
    url: `user/v1/auth/user_profile/preset-gradients?limit=${limit}&offset=${offset}&type=${type}`,
    headers    
  })
  return response
}

export const updateSelectedImage = async (data) => {
    const headers = getApiHeaders()
    return await userAppApiRequest({
        method: 'patch',
        url: `user/v1/auth/user_profile/banner`,
        data: data,
        headers
    })
}

export const uploadedImage = async (formData) => {
    const headers = getApiHeaders()

     if (headers['Content-Type']) {
    delete headers['Content-Type']
  }
    return await userAppApiRequest({
        method: 'post',
        url: `asset/v1/asset-manager/public-upload`,
        data: formData,
        headers
    })
}
export async function deleteDrafts(user_id, draft_ids) {
  const ids = Array.isArray(draft_ids) ? draft_ids : [draft_ids];

  const assetResponse = await assetApiRequest({
    method: 'delete',
    url: '/asset-manager/saved-draft',
    data: {
      user_id: user_id,
      draft_ids: ids
    }
  });

  const socialResponse = await socialApiRequest({
    method: 'delete',
    url: '/saved-draft-metadata',
    data: {
      draft_ids: ids
    }
  });

  return { assetResponse, socialResponse };
}
